/**
 * Course settings · "Înscriere și preț": what the visitor meets at checkout —
 * the checkout mode, the programme's status, the price, the payment deadline
 * and the texts of the checkout and of the pages after it.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Settings, ToggleLeft, ToggleRight, AlertTriangle, CheckCircle2, Tag } from "lucide-react";
import PaymentDeadlineCard from "../PaymentDeadlineCard";
import { Card, TextField } from "./shared";

export default function EnrolmentSettings({ data, save, canMutate, busy }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="settings-tab-inscriere">
      <CheckoutModeCard data={data} save={save} canMutate={canMutate} busy={busy} />
      <ProgramStatusCard data={data} save={save} canMutate={canMutate} busy={busy} />
      <PricingCard data={data} save={save} canMutate={canMutate} busy={busy} />
      <PaymentDeadlineCard />
      <TextsCard data={data} save={save} canMutate={canMutate} busy={busy} />
    </div>
  );
}

const MODE_LABELS = {
  online_payment: "Înscriere + plată online, cu alegerea grupei",
  online_payment_no_group: "Înscriere + plată online, fără alegerea grupei",
  registration_only: "Doar înscriere, plata se trimite ulterior",
};

const MODE_DESCRIPTIONS = {
  online_payment:
    "Participanții aleg o grupă activă, completează formularul și sunt redirecționați imediat către plată.",
  online_payment_no_group:
    "Participanții plătesc imediat și își exprimă preferințele de program, fără să aleagă o grupă. "
    + "Înscrierile apar marcate „Necesită atribuire”, iar grupa se stabilește ulterior din dashboard. "
    + "Folosește-l când încă nu ai grupe deschise.",
  registration_only:
    "Participanții trimit formularul fără plată. Echipa poate trimite ulterior un link de plată din dashboard.",
};

const STATUS_LABELS = {
  in_accreditation: "În proces de acreditare",
  accredited: "Acreditat",
  paused: "Indisponibil temporar",
};

function CheckoutModeCard({ data, save, canMutate, busy }) {
  return (
    <Card title="Mod checkout public" icon={<Settings size={16} />} testid="settings-card-checkout-mode">
      <p className="text-sm text-[#6B7280] mb-3">
        Controlează DOAR ce vede vizitatorul la <code className="bg-[#EDEEF0] px-1 rounded text-xs">/checkout</code>.
        Disponibilitatea efectivă a plăților online se setează separat, în tabul „Plăți și facturi”.
      </p>
      <div className="space-y-2">
        {Object.entries(MODE_LABELS).map(([key, label]) => (
          <label key={key} className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer ${data.checkout_mode === key ? "border-[#965EA7] bg-[#965EA7]/[0.04]" : "border-[#E5E7EB] hover:border-[#965EA7]/40"}`}>
            <input
              type="radio"
              name="checkout_mode"
              value={key}
              checked={data.checkout_mode === key}
              onChange={() => save({ checkout_mode: key }, "mod checkout")}
              disabled={!canMutate || busy !== null}
              className="mt-1 accent-[#965EA7]"
              data-testid={`settings-mode-${key}`}
            />
            <div className="text-sm text-[#373A45]">
              <div className="font-semibold">{label}</div>
              <div className="text-xs text-[#6B7280] mt-0.5">
                {MODE_DESCRIPTIONS[key]}
              </div>
            </div>
          </label>
        ))}
      </div>
      {["online_payment", "online_payment_no_group"].includes(data.checkout_mode)
        && !data.online_payment_ready && (
        <div className="mt-3 text-xs flex items-start gap-2 rounded-lg bg-[#FDF3E7] text-[#8a6a1a] px-3 py-2" data-testid="settings-online-payment-warning">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            Ai ales un mod cu plată online, dar providerul nu este complet activ
            (lipsesc cheile sau comutatorul plăților online este oprit). Vizitatorii NU vor putea
            plăti direct în checkout până nu activezi providerul, din tabul „Plăți și facturi”.
          </span>
        </div>
      )}
    </Card>
  );
}

function ProgramStatusCard({ data, save, canMutate, busy }) {
  return (
    <Card title="Status program" icon={<CheckCircle2 size={16} />} testid="settings-card-program-status">
      <div className="space-y-2">
        {Object.entries(STATUS_LABELS).map(([key, label]) => (
          <label key={key} className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer ${data.program_status === key ? "border-[#965EA7] bg-[#965EA7]/[0.04]" : "border-[#E5E7EB] hover:border-[#965EA7]/40"}`}>
            <input
              type="radio"
              name="program_status"
              value={key}
              checked={data.program_status === key}
              onChange={() => save({ program_status: key }, "status program")}
              disabled={!canMutate || busy !== null}
              className="accent-[#965EA7]"
              data-testid={`settings-program-${key}`}
            />
            <span className="text-sm text-[#373A45] font-medium">{label}</span>
          </label>
        ))}
      </div>
    </Card>
  );
}

function PricingCard({ data, save, canMutate, busy }) {
  // Local drafts so the user can type without triggering save on each keystroke.
  const [standard, setStandard] = useState(data.course_standard_price ?? "");
  const [discount, setDiscount] = useState(data.course_discount_price ?? data.price ?? "");
  const [badge, setBadge] = useState(data.course_discount_badge || "");

  useEffect(() => {
    setStandard(data.course_standard_price ?? "");
    setDiscount(data.course_discount_price ?? data.price ?? "");
    setBadge(data.course_discount_badge || "");
  }, [data.course_standard_price, data.course_discount_price, data.course_discount_badge, data.price]);

  const standardDirty = String(standard) !== String(data.course_standard_price ?? "");
  const discountDirty = String(discount) !== String(data.course_discount_price ?? data.price ?? "");
  const badgeDirty = (badge || "") !== (data.course_discount_badge || "");

  const parsedDiscount = discount === "" || discount === null ? null : Number(discount);
  const parsedStandard = standard === "" || standard === null ? null : Number(standard);
  const hasAnchor = parsedStandard !== null && parsedDiscount !== null && parsedStandard > parsedDiscount;
  const savings = hasAnchor ? parsedStandard - parsedDiscount : 0;
  const savingsPct = hasAnchor ? Math.round((savings / parsedStandard) * 100) : 0;

  const saveStandard = () => {
    const v = standard === "" ? null : Number(standard);
    if (v !== null && (Number.isNaN(v) || v < 0)) {
      toast.error("Preț standard invalid.");
      return;
    }
    save({ course_standard_price: v }, "preț standard");
  };
  const saveDiscount = () => {
    const v = discount === "" ? null : Number(discount);
    if (v !== null && (Number.isNaN(v) || v < 0)) {
      toast.error("Preț promoțional invalid.");
      return;
    }
    save({ course_discount_price: v }, "preț promoțional");
  };
  const saveBadge = () => save({ course_discount_badge: badge.trim() }, "badge promoțional");

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 lg:col-span-2" data-testid="settings-card-pricing">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#965EA7] mb-3">
        <Tag size={16} /> Prețuri
      </div>

      <div className="rounded-xl bg-[#F0E7F4]/40 border border-[#965EA7]/20 p-3 text-xs text-[#373A45] mb-4 leading-relaxed">
        <strong>Prețul promoțional</strong> este folosit în checkout, plăți și facturi.{" "}
        <strong>Prețul standard</strong> este doar pentru afișare/marketing (apare tăiat lângă prețul actual).
        Dacă lași prețul promoțional gol, checkout-ul revine la prețul standard.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PriceField
          label="Preț standard (anchor / strikethrough)"
          hint="Apare tăiat în pricing card. Lasă gol dacă nu vrei reducere afișată."
          value={standard}
          onChange={setStandard}
          onSave={saveStandard}
          dirty={standardDirty}
          canMutate={canMutate}
          busy={busy}
          testid="pricing-standard-input"
        />
        <PriceField
          label="Preț promoțional (folosit la plată)"
          hint="Acesta este prețul real pe care îl plătește utilizatorul."
          value={discount}
          onChange={setDiscount}
          onSave={saveDiscount}
          dirty={discountDirty}
          canMutate={canMutate}
          busy={busy}
          testid="pricing-discount-input"
        />
      </div>

      <div className="mt-4">
        <label className="text-xs font-semibold uppercase tracking-widest text-[#373A45]">
          Badge promoțional
        </label>
        <div className="mt-1 flex gap-2">
          <input
            type="text"
            value={badge}
            onChange={(e) => setBadge(e.target.value)}
            disabled={!canMutate || busy !== null}
            placeholder='ex: "Reducere lansare", "Ofertă limitată", "Early bird"'
            className="flex-1 bg-[#F5F6F6] border border-[#E5E7EB] rounded-xl px-3 py-2 text-sm text-[#373A45] focus:ring-2 focus:ring-[#965EA7] outline-none disabled:opacity-60"
            data-testid="pricing-badge-input"
          />
          {badgeDirty && (
            <button
              onClick={saveBadge}
              disabled={!canMutate || busy !== null}
              className="text-xs rounded-full bg-[#965EA7] text-white px-4 py-1.5 inline-flex items-center gap-1 hover:bg-[#7A4986] disabled:opacity-60"
              data-testid="pricing-badge-save"
            >
              <Save size={12} /> Salvează
            </button>
          )}
        </div>
      </div>

      {/* Live preview */}
      <div className="mt-5 rounded-xl border border-dashed border-[#965EA7]/40 p-4" data-testid="pricing-preview">
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#965EA7]/70 mb-2">
          Preview landing page
        </div>
        {hasAnchor && (
          <div className="flex items-center gap-2 text-xs text-[#6B7280] mb-1">
            <span className="uppercase tracking-widest font-semibold">Preț standard</span>
            <span className="line-through text-[#9CA3AF]">{Number(parsedStandard).toLocaleString("ro-RO")} {data.currency}</span>
          </div>
        )}
        <div className="flex items-end gap-2">
          <div className="font-display text-4xl font-bold text-[#373A45]">
            {parsedDiscount !== null ? Number(parsedDiscount).toLocaleString("ro-RO") : (parsedStandard !== null ? Number(parsedStandard).toLocaleString("ro-RO") : "—")}
          </div>
          <div className="text-[#6B7280] text-sm mb-1">{data.currency}</div>
        </div>
        {hasAnchor && (
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#F0E7F4] text-[#965EA7] px-3 py-1 text-xs font-semibold">
            {(badge || "Reducere lansare")} · economisești {savings.toLocaleString("ro-RO")} {data.currency}
            {savingsPct >= 5 && <span className="text-[#965EA7]/70">· ~{savingsPct}%</span>}
          </div>
        )}
        {!hasAnchor && parsedStandard !== null && parsedDiscount !== null && (
          <div className="mt-2 text-xs text-[#9b1f2c]">
            Prețul promoțional este ≥ prețul standard. UI-ul nu va afișa reducere.
          </div>
        )}
      </div>

      {/* Countdown toggle placeholder */}
      <div className="mt-5 pt-4 border-t border-[#E5E7EB] flex items-center justify-between" data-testid="pricing-countdown-row">
        <div className="text-sm text-[#373A45]">
          <div className="font-semibold flex items-center gap-2">
            Afișează countdown promoțional
            <span className="text-[10px] rounded-full bg-[#EDEEF0] text-[#6B7280] px-2 py-0.5 uppercase tracking-widest">În curând</span>
          </div>
          <div className="text-xs text-[#6B7280]">
            Va activa un timer vizibil pe landing page când îți va aproba lansarea.
          </div>
        </div>
        <button
          disabled={!canMutate || busy !== null}
          onClick={() => save({ course_discount_countdown_enabled: !data.course_discount_countdown_enabled }, "countdown promoțional")}
          className="text-[#965EA7] disabled:opacity-40"
          data-testid="pricing-countdown-toggle"
        >
          {data.course_discount_countdown_enabled ? <ToggleRight size={36} /> : <ToggleLeft size={36} />}
        </button>
      </div>
    </div>
  );
}

function PriceField({ label, hint, value, onChange, onSave, dirty, canMutate, busy, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-widest text-[#373A45]">{label}</label>
      <div className="mt-1 flex gap-2">
        <input
          type="number"
          min="0"
          step="1"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={!canMutate || busy !== null}
          placeholder="ex: 1490"
          className="flex-1 bg-[#F5F6F6] border border-[#E5E7EB] rounded-xl px-3 py-2 text-sm text-[#373A45] focus:ring-2 focus:ring-[#965EA7] outline-none disabled:opacity-60"
          data-testid={testid}
        />
        {dirty && (
          <button
            onClick={onSave}
            disabled={!canMutate || busy !== null}
            className="text-xs rounded-full bg-[#965EA7] text-white px-4 py-1.5 inline-flex items-center gap-1 hover:bg-[#7A4986] disabled:opacity-60"
            data-testid={`${testid}-save`}
          >
            <Save size={12} /> Salvează
          </button>
        )}
      </div>
      {hint && <div className="text-xs text-[#6B7280] mt-1">{hint}</div>}
    </div>
  );
}

function TextsCard({ data, save, canMutate, busy }) {
  return (
    <Card title="Texte personalizate" icon={<Settings size={16} />} testid="settings-card-texts">
      <TextField label="Mesaj intro checkout" value={data.checkout_intro_text} onSave={(v) => save({ checkout_intro_text: v }, "mesaj checkout")} canMutate={canMutate} busy={busy} />
      <TextField label="Mesaj success – plată" value={data.success_text_paid} onSave={(v) => save({ success_text_paid: v }, "mesaj success plată")} canMutate={canMutate} busy={busy} />
      <TextField label="Mesaj success – înscriere" value={data.success_text_registration} onSave={(v) => save({ success_text_registration: v }, "mesaj success înscriere")} canMutate={canMutate} busy={busy} />
    </Card>
  );
}
